import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  MessageSquare,
  Briefcase,
  ShieldCheck,
  LogOut,
  Settings,
  Building2,
  Package,
  Wand2,
  Network,
  Folder,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/chiedi", label: "Cosa creare", icon: MessageSquare, testId: "nav-chiedi" },
  { to: "/settori", label: "Per reparto", icon: Folder, testId: "nav-settori" },
  { to: "/catalogo", label: "Ricette", icon: LayoutGrid, testId: "nav-catalogo" },
  { to: "/soluzioni", label: "Soluzioni OSS", icon: Package, testId: "nav-soluzioni" },
  { to: "/componi", label: "App composable", icon: Network, testId: "nav-componi" },
  { to: "/costruisci/le-mie", label: "App custom", icon: Wand2, testId: "nav-costruisci" },
  { to: "/lavori", label: "Lavori", icon: Briefcase, testId: "nav-lavori" },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Amministrazione", icon: ShieldCheck, testId: "nav-admin" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const orgName = user?.organization?.name || "Confindustria";
  const initials = (user?.full_name || user?.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F2F5F8]">
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex md:flex-col w-64 bg-white border-r border-[#E2E8F0]"
      >
        <div className="px-5 py-5 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold"
              style={{ background: "#0F3D6B" }}
            >
              A
            </div>
            <div>
              <div className="serif text-[17px] font-semibold leading-tight tracking-tight text-[#1A1A1A]">
                ADRIAI Builder
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#555555]">
                Confindustria
              </div>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[#F2F5F8]">
            <Building2 className="w-4 h-4 text-[#0F3D6B]" />
            <div className="text-[13px] text-[#1A1A1A] truncate" data-testid="org-name">
              {orgName}
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                `adr-sidebar-link ${isActive ? "active" : ""}`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" &&
            ADMIN_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `adr-sidebar-link ${isActive ? "active" : ""}`
                }
              >
                <n.icon className="w-4 h-4" />
                <span>{n.label}</span>
              </NavLink>
            ))}
        </nav>

        <div className="px-3 py-3 border-t border-[#E2E8F0]">
          <NavLink
            to="/impostazioni"
            className="adr-sidebar-link mb-2"
            data-testid="nav-impostazioni"
          >
            <Settings className="w-4 h-4" />
            <span>Impostazioni</span>
          </NavLink>
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold"
              style={{ background: "#0F3D6B" }}
              data-testid="user-avatar"
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-[#1A1A1A] truncate">
                {user?.full_name || user?.email}
              </div>
              <div className="text-[11px] text-[#555555] truncate capitalize">
                {user?.role}
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="text-[#555555] hover:text-[#C8102E] transition-colors p-1.5 rounded-md hover:bg-[#F2F5F8]"
              aria-label="Esci"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold"
              style={{ background: "#0F3D6B" }}
            >
              A
            </div>
            <span className="serif font-semibold text-[#1A1A1A]">ADRIAI</span>
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            data-testid="logout-button-mobile"
            className="text-[#555555]"
            aria-label="Esci"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="md:hidden bg-white border-b border-[#E2E8F0] px-4 py-2 flex gap-4 overflow-x-auto">
          {[...NAV, ...(user?.role === "admin" ? ADMIN_NAV : [])].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-sm whitespace-nowrap py-1.5 px-2 rounded ${
                  isActive
                    ? "text-[#0F3D6B] font-semibold bg-[#F2F5F8]"
                    : "text-[#555555]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
